import { useState, useEffect } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import { getLang, setLang, setCurrentLang, type Lang } from '../i18n';

const languages: { code: Lang; label: string; dir: 'ltr' | 'rtl' }[] = [
  { code: 'en', label: 'English', dir: 'ltr' },
  { code: 'ar', label: 'العربية', dir: 'rtl' },
  { code: 'he', label: 'עברית', dir: 'rtl' },
];

export default function LanguageSwitcher() {
  const [currentLang, setCurrentLangState] = useState<Lang>(getLang());
  const [isOpen, setIsOpen] = useState(false);

  // Sync with i18n system on mount
  useEffect(() => {
    const lang = getLang();
    setCurrentLangState(lang);
    setCurrentLang(lang);
  }, []);

  const handleSelect = (lang: Lang) => {
    setLang(lang);
    setCurrentLang(lang);
    setCurrentLangState(lang);
    setIsOpen(false);
    // Force re-render of entire app
    window.location.reload();
  };

  const currentLanguage = languages.find(l => l.code === currentLang) || languages[0];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition"
        title="Change Language"
      >
        <Globe className="w-4 h-4" />
        <span className="hidden sm:inline">{currentLanguage.label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)} 
          />
          
          {/* Dropdown */}
          <div className="absolute top-full mt-1 right-0 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-50 min-w-[140px] overflow-hidden">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleSelect(lang.code)}
                className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-700 transition flex items-center justify-between ${
                  lang.code === currentLang
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-300'
                }`}
                dir={lang.dir}
              >
                <span>{lang.label}</span>
                {lang.code === currentLang && (
                  <span className="text-xs">✓</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
